/* eslint-disable max-lines */
import {
	Camera,
	Color,
	HemisphereLight,
	Mesh,
	MeshStandardMaterial,
	Raycaster,
	Scene,
	SphereGeometry,
	Vector2
} from 'three';

import { angleToColor, calculateDragAngle } from './color-utils';
import { applyColorWheelToSphere, revertToSolidColor } from './create-color-sphere';
import { LightHelper } from './light-helper.class';
import {
	createInitialInteractionState,
	HemisphereSphereType,
	InteractionEndCallback,
	InteractionStartCallback,
	LIGHT_INTERACTION_DEFAULTS,
	LightColorChangeCallback,
	LightInteractionConfig,
	LightInteractionState
} from './light-interaction.types';
import { isHemisphereLightObjects, VisualizableLight } from './light-helper.types';
import { updateGradientRingThickness } from './visualize-hemisphere-light';

/** Minimum intensity value */
const MIN_INTENSITY = 0;
/** Maximum intensity value */
const MAX_INTENSITY = 3.0;
/** Drag distance for full intensity range (in pixels) */
const INTENSITY_DRAG_RANGE = 200;
/** Minimum drag distance to be considered a drag (vs click) */
const MIN_DRAG_DISTANCE = 5;

/**
 * Callback when light intensity changes
 */
export type LightIntensityChangeCallback = (intensity: number) => void;

/**
 * Callback when light is toggled on/off
 */
export type LightToggleCallback = (isOn: boolean) => void;

/**
 * Manages interactive color picking on light helper spheres
 *
 * Handles mouse and touch events to allow users to:
 * - Click/tap on a light sphere to activate color picker
 * - Drag to rotate through colors
 * - Release to confirm color selection
 * - For hemisphere light control sphere: click toggles on/off, drag changes intensity
 */
export class LightInteraction {
	private scene: Scene;
	private camera: Camera;
	private domElement: HTMLElement;
	private raycaster: Raycaster;
	private pointer: Vector2;
	private state: LightInteractionState;
	private config: Required<LightInteractionConfig>;

	/** Map of sphere mesh to LightHelper */
	private sphereToHelper: Map<Mesh, LightHelper> = new Map();

	/** Map of sphere mesh to Light */
	private sphereToLight: Map<Mesh, VisualizableLight> = new Map();

	/** Map of sphere mesh to hemisphere sphere type (for hemisphere lights) */
	private sphereToHemisphereType: Map<Mesh, HemisphereSphereType> = new Map();

	/** Callback when color changes are confirmed */
	private onColorChange: LightColorChangeCallback | null = null;

	/** Callback when intensity changes (for control sphere) */
	private onIntensityChange: LightIntensityChangeCallback | null = null;

	/** Callback when light is toggled (for control sphere) */
	private onLightToggle: LightToggleCallback | null = null;

	/** Callback when interaction starts */
	private onInteractionStart: InteractionStartCallback | null = null;

	/** Callback when interaction ends */
	private onInteractionEnd: InteractionEndCallback | null = null;

	/** Touch hold timer for detecting hold gesture */
	private touchHoldTimer: ReturnType<typeof setTimeout> | null = null;
	private readonly TOUCH_HOLD_DELAY = 100; // ms to detect hold (quick activation)

	/** Screen center for rotation calculations */
	private screenCenter: Vector2 = new Vector2();

	/** Flag to suppress click events after interaction ends */
	private clickSuppressed = false;
	private clickSuppressTimer: ReturnType<typeof setTimeout> | null = null;
	private readonly CLICK_SUPPRESS_DELAY = 300; // ms to suppress click after interaction

	constructor (
		scene: Scene,
		camera: Camera,
		domElement: HTMLElement,
		config: LightInteractionConfig = {}
	) {
		this.scene = scene;
		this.camera = camera;
		this.domElement = domElement;
		this.raycaster = new Raycaster();
		this.pointer = new Vector2();
		this.state = createInitialInteractionState();

		this.config = {
			activeScaleMultiplier: config.activeScaleMultiplier ?? LIGHT_INTERACTION_DEFAULTS.ACTIVE_SCALE_MULTIPLIER,
			rotationSensitivity: config.rotationSensitivity ?? LIGHT_INTERACTION_DEFAULTS.ROTATION_SENSITIVITY,
			showConePreview: config.showConePreview ?? LIGHT_INTERACTION_DEFAULTS.SHOW_CONE_PREVIEW
		};

		this.bindEvents();
		this.updateScreenCenter();
	}

	/**
	 * Register a light helper for interaction
	 */
	registerHelper (helper: LightHelper): void {
		this.sphereToHelper.set(helper.sphere, helper);
		this.sphereToLight.set(helper.sphere, helper.light);

		// For hemisphere lights, also register sky, ground, and control spheres
		if (helper.isHemisphereLight && helper.skySphere && helper.groundSphere && helper.controlSphere) {
			// Register sky sphere
			this.sphereToHelper.set(helper.skySphere, helper);
			this.sphereToLight.set(helper.skySphere, helper.light);
			this.sphereToHemisphereType.set(helper.skySphere, 'sky');

			// Register ground sphere
			this.sphereToHelper.set(helper.groundSphere, helper);
			this.sphereToLight.set(helper.groundSphere, helper.light);
			this.sphereToHemisphereType.set(helper.groundSphere, 'ground');

			// Register control sphere
			this.sphereToHelper.set(helper.controlSphere, helper);
			this.sphereToLight.set(helper.controlSphere, helper.light);
			this.sphereToHemisphereType.set(helper.controlSphere, 'control');
		}
	}

	/**
	 * Unregister a light helper
	 */
	unregisterHelper (helper: LightHelper): void {
		this.sphereToHelper.delete(helper.sphere);
		this.sphereToLight.delete(helper.sphere);

		// For hemisphere lights, also unregister sky, ground, and control spheres
		if (helper.isHemisphereLight && helper.skySphere && helper.groundSphere && helper.controlSphere) {
			this.sphereToHelper.delete(helper.skySphere);
			this.sphereToLight.delete(helper.skySphere);
			this.sphereToHemisphereType.delete(helper.skySphere);

			this.sphereToHelper.delete(helper.groundSphere);
			this.sphereToLight.delete(helper.groundSphere);
			this.sphereToHemisphereType.delete(helper.groundSphere);

			this.sphereToHelper.delete(helper.controlSphere);
			this.sphereToLight.delete(helper.controlSphere);
			this.sphereToHemisphereType.delete(helper.controlSphere);
		}
	}

	/**
	 * Check if click events should be suppressed (after color wheel interaction)
	 * Returns true and clears the flag if suppressed
	 */
	shouldSuppressClick (): boolean {
		if (this.clickSuppressed) {
			return true;
		}
		return false;
	}

	/**
	 * Check if interaction is currently active
	 */
	isInteractionActive (): boolean {
		return this.state.isActive;
	}

	/**
	 * Set callback for when color changes are confirmed
	 */
	setOnColorChange (callback: LightColorChangeCallback | null): void {
		this.onColorChange = callback;
	}

	/**
	 * Set callback for when intensity changes (control sphere drag)
	 */
	setOnIntensityChange (callback: LightIntensityChangeCallback | null): void {
		this.onIntensityChange = callback;
	}

	/**
	 * Set callback for when light is toggled (control sphere click)
	 */
	setOnLightToggle (callback: LightToggleCallback | null): void {
		this.onLightToggle = callback;
	}

	/**
	 * Set callback for when interaction starts (user begins dragging)
	 * Use this to disable camera controls, etc.
	 */
	setOnInteractionStart (callback: InteractionStartCallback | null): void {
		this.onInteractionStart = callback;
	}

	/**
	 * Set callback for when interaction ends (user releases)
	 * Use this to re-enable camera controls, etc.
	 */
	setOnInteractionEnd (callback: InteractionEndCallback | null): void {
		this.onInteractionEnd = callback;
	}

	/**
	 * Update screen center (call on resize)
	 */
	updateScreenCenter (): void {
		const rect = this.domElement.getBoundingClientRect();
		this.screenCenter.set(
			rect.left + rect.width / 2,
			rect.top + rect.height / 2
		);
	}

	/**
	 * Check if interaction is currently active
	 */
	get isActive (): boolean {
		return this.state.isActive;
	}

	/**
	 * Get the current temporary color
	 */
	get tempColor (): Color {
		return this.state.tempColor.clone();
	}

	/**
	 * Bind event listeners
	 */
	private bindEvents (): void {
		// Mouse events
		this.domElement.addEventListener('mousedown', this.handleMouseDown);
		this.domElement.addEventListener('mousemove', this.handleMouseMove);
		this.domElement.addEventListener('mouseup', this.handleMouseUp);
		this.domElement.addEventListener('mouseleave', this.handleMouseUp);

		// Touch events
		this.domElement.addEventListener('touchstart', this.handleTouchStart, { passive: false });
		this.domElement.addEventListener('touchmove', this.handleTouchMove, { passive: false });
		this.domElement.addEventListener('touchend', this.handleTouchEnd);
		this.domElement.addEventListener('touchcancel', this.handleTouchEnd);

		// Resize
		window.addEventListener('resize', this.handleResize);
	}

	/**
	 * Unbind event listeners
	 */
	private unbindEvents (): void {
		this.domElement.removeEventListener('mousedown', this.handleMouseDown);
		this.domElement.removeEventListener('mousemove', this.handleMouseMove);
		this.domElement.removeEventListener('mouseup', this.handleMouseUp);
		this.domElement.removeEventListener('mouseleave', this.handleMouseUp);

		this.domElement.removeEventListener('touchstart', this.handleTouchStart);
		this.domElement.removeEventListener('touchmove', this.handleTouchMove);
		this.domElement.removeEventListener('touchend', this.handleTouchEnd);
		this.domElement.removeEventListener('touchcancel', this.handleTouchEnd);

		window.removeEventListener('resize', this.handleResize);
	}

	/**
	 * Update pointer position from event
	 */
	private updatePointer (clientX: number, clientY: number): void {
		const rect = this.domElement.getBoundingClientRect();
		this.pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
		this.pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
	}

	/**
	 * Check if pointer is over a registered sphere
	 */
	private checkSphereHit (): Mesh | null {
		this.raycaster.setFromCamera(this.pointer, this.camera);
		const spheres = Array.from(this.sphereToHelper.keys());
		const intersects = this.raycaster.intersectObjects(spheres, false);

		if (intersects.length > 0) {
			return intersects[0].object as Mesh;
		}
		return null;
	}

	/**
	 * Start interaction on a sphere
	 */
	private startInteraction (sphere: Mesh, clientX: number, clientY: number): void {
		const light = this.sphereToLight.get(sphere);
		if (!light) return;

		this.state.isActive = true;
		this.state.targetSphere = sphere;
		this.state.startPosition.set(clientX, clientY);
		this.state.currentPosition.set(clientX, clientY);
		this.state.rotationAngle = 0;
		this.state.originalScale = sphere.scale.x;
		this.state.isDragging = false;

		// Check if this is a hemisphere light sphere
		const hemisphereType = this.sphereToHemisphereType.get(sphere);
		this.state.hemisphereSphereType = hemisphereType || null;

		// Store original intensity for control sphere
		this.state.originalIntensity = light.intensity;
		this.state.tempIntensity = light.intensity;

		// Get the correct color based on sphere type
		if (hemisphereType === 'ground' && light instanceof HemisphereLight) {
			this.state.originalColor.copy(light.groundColor);
			this.state.tempColor.copy(light.groundColor);
		} else if (hemisphereType === 'control') {
			// Control sphere doesn't change color, just intensity
			// Use a blend of sky and ground for visual
			if (light instanceof HemisphereLight) {
				const blendColor = new Color();
				blendColor.r = (light.color.r + light.groundColor.r) / 2;
				blendColor.g = (light.color.g + light.groundColor.g) / 2;
				blendColor.b = (light.color.b + light.groundColor.b) / 2;
				this.state.originalColor.copy(blendColor);
				this.state.tempColor.copy(blendColor);
			}
		} else {
			this.state.originalColor.copy(light.color);
			this.state.tempColor.copy(light.color);
		}

		// For control sphere, don't apply color wheel - just scale up
		if (hemisphereType === 'control') {
			// Enlarge sphere slightly for feedback
			const newScale = this.state.originalScale * this.config.activeScaleMultiplier;
			sphere.scale.setScalar(newScale);
		} else {
			// Enlarge sphere by 10%
			const newScale = this.state.originalScale * this.config.activeScaleMultiplier;
			sphere.scale.setScalar(newScale);

			// Change to color wheel
			applyColorWheelToSphere(sphere);

			// Rotate sphere to show current color at "front"
			this.updateSphereRotation();
		}

		// Notify that interaction has started
		this.onInteractionStart?.();
	}

	/**
	 * Update interaction during drag
	 */
	private updateInteraction (clientX: number, clientY: number): void {
		if (!this.state.isActive || !this.state.targetSphere) return;

		const previousPosition = this.state.currentPosition.clone();
		this.state.currentPosition.set(clientX, clientY);

		// Calculate total drag distance from start
		const totalDragDistance = Math.sqrt(
			Math.pow(clientX - this.state.startPosition.x, 2) +
			Math.pow(clientY - this.state.startPosition.y, 2)
		);

		// Mark as dragging if moved beyond threshold
		if (totalDragDistance > MIN_DRAG_DISTANCE) {
			this.state.isDragging = true;
		}

		// Handle control sphere differently - vertical drag changes intensity
		if (this.state.hemisphereSphereType === 'control') {
			this.updateControlSphereInteraction(clientY - previousPosition.y);
			return;
		}

		// Calculate delta movement for color spheres
		const deltaX = clientX - previousPosition.x;
		const deltaY = clientY - previousPosition.y;

		// Rotation sensitivity - higher value = more responsive
		const sensitivity = this.config.rotationSensitivity * 0.02;

		// Apply rotation based on drag direction
		// Horizontal drag rotates around Y axis (changes hue)
		// Vertical drag rotates around X axis (visual effect)
		this.state.targetSphere.rotation.y += deltaX * sensitivity;
		this.state.targetSphere.rotation.x += deltaY * sensitivity;

		// Track total Y rotation for color calculation
		this.state.rotationAngle = this.state.targetSphere.rotation.y;

		// Update temp color based on Y rotation (hue)
		this.state.tempColor = angleToColor(this.state.rotationAngle);

		// Update light and cone preview
		this.updateLightPreview();
	}

	/**
	 * Update control sphere interaction (drag to change intensity)
	 */
	private updateControlSphereInteraction (deltaY: number): void {
		const light = this.sphereToLight.get(this.state.targetSphere!);
		const helper = this.sphereToHelper.get(this.state.targetSphere!);
		if (!light) return;

		// Vertical drag: up = increase intensity, down = decrease
		// Invert deltaY because screen Y increases downward
		const intensityDelta = -deltaY / INTENSITY_DRAG_RANGE * MAX_INTENSITY;
		this.state.tempIntensity = Math.max(MIN_INTENSITY, Math.min(MAX_INTENSITY,
			this.state.tempIntensity + intensityDelta
		));

		// Update light intensity in real-time
		light.intensity = this.state.tempIntensity;

		// Update control sphere opacity to reflect intensity
		const controlSphereMaterial = this.state.targetSphere!.material as MeshStandardMaterial;
		controlSphereMaterial.opacity = 0.3 + (this.state.tempIntensity / MAX_INTENSITY) * 0.5;
		controlSphereMaterial.emissiveIntensity = (this.state.tempIntensity / MAX_INTENSITY) * 0.6;

		// Update gradient ring thickness to reflect intensity
		if (helper && isHemisphereLightObjects((helper as any).objects || {})) {
			// Access the helper's internal objects to update ring thickness
			const helperAny = helper as any;
			if (helperAny.objects && helperAny.objects.gradientRings) {
				const sphereRadius = (helperAny.objects.skySphere.geometry as SphereGeometry).parameters.radius;
				updateGradientRingThickness(helperAny.objects, this.state.tempIntensity, sphereRadius, helperAny._quality || 5);
			}
		}

		// Notify intensity change
		this.onIntensityChange?.(this.state.tempIntensity);
	}

	/**
	 * Update sphere rotation to show current color
	 */
	private updateSphereRotation (): void {
		if (!this.state.targetSphere) return;

		// Initial rotation is set in updateInteraction
		// This method can be used for initial setup if needed
	}

	/**
	 * Update light and cone with preview color
	 */
	private updateLightPreview (): void {
		if (!this.state.targetSphere) return;

		const light = this.sphereToLight.get(this.state.targetSphere);
		const helper = this.sphereToHelper.get(this.state.targetSphere);

		if (light && this.config.showConePreview) {
			// For hemisphere lights, update the correct color property
			if (this.state.hemisphereSphereType === 'ground' && light instanceof HemisphereLight) {
				light.groundColor.copy(this.state.tempColor);
			} else if (this.state.hemisphereSphereType !== 'control') {
				// Don't change color for control sphere
				light.color.copy(this.state.tempColor);
			}
		}

		if (helper && this.config.showConePreview && helper.cone) {
			// Update cone color (for spotlight helpers)
			const coneMaterial = helper.cone.material as MeshStandardMaterial;
			coneMaterial.color.copy(this.state.tempColor);
		}
	}

	/**
	 * End interaction and confirm color/intensity
	 */
	private endInteraction (confirm: boolean): void {
		if (!this.state.isActive || !this.state.targetSphere) return;

		const sphere = this.state.targetSphere;
		const light = this.sphereToLight.get(sphere);
		const helper = this.sphereToHelper.get(sphere);
		const hemisphereType = this.state.hemisphereSphereType;

		// Handle control sphere specially
		if (hemisphereType === 'control' && light) {
			this.endControlSphereInteraction(confirm, light, sphere, helper);
			return;
		}

		if (confirm && light) {
			// Confirm the color change - use correct property for hemisphere lights
			if (hemisphereType === 'ground' && light instanceof HemisphereLight) {
				light.groundColor.copy(this.state.tempColor);
			} else {
				light.color.copy(this.state.tempColor);
			}
			this.onColorChange?.(this.state.tempColor.clone());
		} else if (light) {
			// Revert to original color
			if (hemisphereType === 'ground' && light instanceof HemisphereLight) {
				light.groundColor.copy(this.state.originalColor);
			} else {
				light.color.copy(this.state.originalColor);
			}

			if (helper && helper.cone) {
				const coneMaterial = helper.cone.material as MeshStandardMaterial;
				coneMaterial.color.copy(this.state.originalColor);
			}
		}

		// Restore sphere to appropriate color
		// For hemisphere lights, use the actual light color instead of just on/off
		const isLightOn = light ? (light.visible && light.intensity > 0) : true;

		if (hemisphereType && light instanceof HemisphereLight) {
			// Restore to the actual light color for hemisphere spheres
			const sphereMaterial = sphere.material as MeshStandardMaterial;
			if (sphereMaterial.map) {
				// Dispose of the color wheel texture first
				sphereMaterial.map.dispose();
			}
			sphereMaterial.dispose();

			const targetColor = confirm ? this.state.tempColor : this.state.originalColor;
			sphere.material = new MeshStandardMaterial({
				color: isLightOn ? targetColor : 0x333333,
				emissive: isLightOn ? targetColor : new Color(0x000000),
				emissiveIntensity: isLightOn ? 0.3 : 0
			});
		} else {
			// Use standard revert for non-hemisphere lights
			revertToSolidColor(sphere, isLightOn);
		}

		// Restore sphere scale
		sphere.scale.setScalar(this.state.originalScale);

		// Reset all rotations
		sphere.rotation.set(0, 0, 0);

		// Reset state
		this.state = createInitialInteractionState();

		// Suppress click events for a short period after interaction
		this.clickSuppressed = true;
		if (this.clickSuppressTimer) {
			clearTimeout(this.clickSuppressTimer);
		}
		this.clickSuppressTimer = setTimeout(() => {
			this.clickSuppressed = false;
		}, this.CLICK_SUPPRESS_DELAY);

		// Notify that interaction has ended
		this.onInteractionEnd?.();
	}

	/**
	 * End control sphere interaction
	 * Click (no drag) = toggle light on/off
	 * Drag = confirm intensity change
	 */
	private endControlSphereInteraction (
		confirm: boolean,
		light: VisualizableLight,
		sphere: Mesh,
		helper: LightHelper | undefined
	): void {
		const wasDragging = this.state.isDragging;

		if (!wasDragging) {
			// This was a click, not a drag - toggle light on/off
			const wasOn = light.visible && light.intensity > 0;
			if (wasOn) {
				// Turn off - set intensity to 0
				light.intensity = 0;
			} else {
				// Turn on - restore to original intensity or default
				light.intensity = this.state.originalIntensity > 0 ? this.state.originalIntensity : 1;
			}
			this.onLightToggle?.(!wasOn);
		} else if (confirm) {
			// Confirm intensity change
			light.intensity = this.state.tempIntensity;
			this.onIntensityChange?.(this.state.tempIntensity);
		} else {
			// Revert intensity
			light.intensity = this.state.originalIntensity;

			// Revert ring thickness
			if (helper && isHemisphereLightObjects((helper as any).objects || {})) {
				const helperAny = helper as any;
				if (helperAny.objects && helperAny.objects.gradientRings) {
					const sphereRadius = (helperAny.objects.skySphere.geometry as SphereGeometry).parameters.radius;
					updateGradientRingThickness(helperAny.objects, this.state.originalIntensity, sphereRadius, helperAny._quality || 5);
				}
			}
		}

		// Update control sphere appearance
		const isLightOn = light.visible && light.intensity > 0;
		const controlSphereMaterial = sphere.material as MeshStandardMaterial;

		if (light instanceof HemisphereLight) {
			const blendColor = new Color();
			blendColor.r = (light.color.r + light.groundColor.r) / 2;
			blendColor.g = (light.color.g + light.groundColor.g) / 2;
			blendColor.b = (light.color.b + light.groundColor.b) / 2;

			controlSphereMaterial.color.copy(isLightOn ? blendColor : new Color(0x333333));
			controlSphereMaterial.emissive.copy(isLightOn ? blendColor : new Color(0x000000));
			controlSphereMaterial.emissiveIntensity = isLightOn ? 0.4 : 0;
			controlSphereMaterial.opacity = isLightOn ? 0.5 : 0.3;
		}

		// Restore sphere scale
		sphere.scale.setScalar(this.state.originalScale);

		// Reset state
		this.state = createInitialInteractionState();

		// Suppress click events for a short period after interaction
		this.clickSuppressed = true;
		if (this.clickSuppressTimer) {
			clearTimeout(this.clickSuppressTimer);
		}
		this.clickSuppressTimer = setTimeout(() => {
			this.clickSuppressed = false;
		}, this.CLICK_SUPPRESS_DELAY);

		// Notify that interaction has ended
		this.onInteractionEnd?.();
	}

	/**
	 * Cancel interaction (revert to original)
	 */
	cancelInteraction (): void {
		this.endInteraction(false);
	}

	// Event handlers (arrow functions to preserve 'this')

	private handleMouseDown = (event: MouseEvent): void => {
		if (event.button !== 0) return; // Only left mouse button

		this.updatePointer(event.clientX, event.clientY);
		const hitSphere = this.checkSphereHit();

		if (hitSphere) {
			event.preventDefault();
			this.startInteraction(hitSphere, event.clientX, event.clientY);
		}
	};

	private handleMouseMove = (event: MouseEvent): void => {
		if (!this.state.isActive) return;

		event.preventDefault();
		this.updateInteraction(event.clientX, event.clientY);
	};

	private handleMouseUp = (event: MouseEvent): void => {
		if (!this.state.isActive) return;

		event.preventDefault();
		this.endInteraction(true);
	};

	private handleTouchStart = (event: TouchEvent): void => {
		if (event.touches.length !== 1) return;

		const touch = event.touches[0];
		this.updatePointer(touch.clientX, touch.clientY);
		const hitSphere = this.checkSphereHit();

		if (hitSphere) {
			event.preventDefault();

			// Start a timer for touch-hold detection
			this.touchHoldTimer = setTimeout(() => {
				this.startInteraction(hitSphere, touch.clientX, touch.clientY);
			}, this.TOUCH_HOLD_DELAY);
		}
	};

	private handleTouchMove = (event: TouchEvent): void => {
		if (!this.state.isActive) {
			// Cancel hold timer if user moves before hold delay
			if (this.touchHoldTimer) {
				clearTimeout(this.touchHoldTimer);
				this.touchHoldTimer = null;
			}
			return;
		}

		if (event.touches.length !== 1) return;

		event.preventDefault();
		const touch = event.touches[0];
		this.updateInteraction(touch.clientX, touch.clientY);
	};

	private handleTouchEnd = (event: TouchEvent): void => {
		// Clear hold timer if set
		if (this.touchHoldTimer) {
			clearTimeout(this.touchHoldTimer);
			this.touchHoldTimer = null;
		}

		if (!this.state.isActive) return;

		event.preventDefault();
		this.endInteraction(true);
	};

	private handleResize = (): void => {
		this.updateScreenCenter();
	};

	/**
	 * Dispose of resources
	 */
	dispose (): void {
		this.unbindEvents();
		this.cancelInteraction();

		if (this.touchHoldTimer) {
			clearTimeout(this.touchHoldTimer);
		}

		this.sphereToHelper.clear();
		this.sphereToLight.clear();
		this.sphereToHemisphereType.clear();
	}
}
